// stores/poolStore.js
'use client';
import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { normalizeApiResponse } from '../utils/response';
import { apiOslogCrud } from 'helpers/oslogFormatEndpoint';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const revalidatePoolList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/pool/search');
};

const poolApi = apiOslogCrud('pool');
const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const response = await poolApi.search(payload);
  return response;
};

export const usePoolList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;

  const tableName = searchKey === 'country' ? 'm_country' : searchKey === 'area' ? 'm_area' : null;
  const filterLike = searchKey === 'country' || searchKey === 'area' ? 'name' : searchKey;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage);

    if (searchValue && searchValue.trim() !== '') {
      fb.like(filterLike, searchValue, 'AND', tableName);
    }

    fb.addJoin('company', ['name'])
      .addJoin('country', ['name'])
      .addJoin('area', ['name'])
      .addJoin('pool_group', ['name'])
      .setOrder(['name'], true);
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }
    return fb.build();
  }, [start, rowsPerPage, searchValue, companyId, filterLike, tableName]);
  const key = useMemo(() => ['/pool/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getPoolById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/pool/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch pool by ID:', error);
    throw error;
  }
};

export const createPool = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/pool/new${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePoolList();
    return response.data;
  } catch (error) {
    console.error('Error creating pool:', error?.response?.data ?? error);
    throw error;
  }
};

export const updatePool = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/pool/edit/${id}${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePoolList();
    return response.data;
  } catch (error) {
    console.error('Error updating pool:', error?.response?.data ?? error);
    throw error;
  }
};

export const deletePool = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/pool/delete/${id}${getTzParam()}`);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePoolList();
    return response.data;
  } catch (error) {
    console.error('Error deleting pool:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateGeomPool = async (id, geomData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...geomData
    };
    const response = await fleetApi.put(`/pool/edit/${id}${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePoolList();
    return response.data;
  } catch (error) {
    console.error('Error updating pool geometry:', error?.response?.data ?? error);
    throw error;
  }
};
